<template>
  <AppDrawer :model-value="modelValue" :title="drawerTitle" @update:model-value="emit('update:modelValue', $event)">
    <template #eyebrow>
      <p class="font-mono text-2xs uppercase tracking-wide text-accent">{{ messages.drawer.eyebrow }}</p>
    </template>

    <div class="flex h-full min-h-0 flex-col gap-4 overflow-hidden p-4">
      <div v-if="store.raidMapError !== null" class="shrink-0 rounded-lg border border-danger/40 bg-bg-surface px-4 py-3 text-sm text-danger" role="alert">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <span>{{ store.raidMapError }}</span>
          <AppButton :variant="ButtonVariant.Secondary" :size="ButtonSize.Sm" @click="retryLoad">{{ messages.drawer.retry }}</AppButton>
        </div>
      </div>

      <div class="relative min-h-0 min-w-0 flex-1" :aria-busy="store.raidMapLoading ? 'true' : 'false'">
        <div v-if="store.raidMapLoading" class="pointer-events-none absolute inset-x-0 top-0 z-30 flex justify-center p-2" role="status" aria-live="polite">
          <span class="rounded-md border border-border bg-bg-surface px-4 py-2 text-sm text-text-muted shadow-sm">{{ messages.drawer.loading }}</span>
        </div>
        <GvgMapCanvas
          :groups="mapGroups"
          :players="store.currentPlayers"
          :player-name-mode="store.playerNameMode"
          :positions="store.raidMapGroupPositions"
          :tree-position="store.raidMapTreePosition"
          :defense-group-ids="defenseGroupIds"
          :attack-group-ids="attackGroupIds"
          :editable="canEdit && !store.raidMapLoading"
          @move-group="moveGroup"
          @move-tree="moveTree"
          @edit-group="emit('edit-group', $event)"
          @edit-tag="editTag"
        />
      </div>

      <p class="shrink-0 rounded-lg border border-border bg-bg-surface px-4 py-3 text-xs text-text-muted">
        {{ messages.drawer.help }}
      </p>
      <p v-if="mapGroups.length === 0" class="shrink-0 rounded-lg border border-border bg-bg-surface px-4 py-3 text-center text-sm text-text-muted">
        {{ messages.drawer.emptyGroups }}
      </p>
    </div>
  </AppDrawer>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { AppButton, AppDrawer, ButtonSize, ButtonVariant } from '@/shared/ui'
import { defineLocaleCatalog } from '@/shared/i18n'
import { useLocaleMessages } from '@/shared/composables/useI18n'
import { useGvgPlannerStore } from '../../stores/gvgPlannerStore'
import GvgMapCanvas from './GvgMapCanvas.vue'
import { gvgMapEn } from './locales/en.ts'
import { gvgMapRu } from './locales/ru.ts'

export interface GvgMapDrawerProps {
  readonly modelValue: boolean
}

defineProps<GvgMapDrawerProps>()

const emit = defineEmits<{
  (event: 'update:modelValue', value: boolean): void
  /** Stable group id consumed by the existing player-filter drawer. */
  (event: 'edit-group', groupStableId: string): void
  /** Catalog tag id consumed by the existing player-filter drawer. */
  (event: 'edit-tag', tagId: string): void
}>()

const messages = useLocaleMessages(defineLocaleCatalog({ en: gvgMapEn, ru: gvgMapRu }))
const store = useGvgPlannerStore()
const canEdit = computed<boolean>(
  () => (store.isOwner || store.isOfficer) && !store.activeRaidEditLocked,
)
const defenseGroupIds = computed<ReadonlySet<string>>(() => groupIdsForRoom('defense'))
const attackGroupIds = computed<ReadonlySet<string>>(() => groupIdsForRoom('attack'))
const poolGroupIds = computed<ReadonlySet<string>>(() => groupIdsForRoom('pool'))
const mapGroups = computed(() =>
  store.raidGroups.filter((group) => !poolGroupIds.value.has(group.stableId)),
)
const drawerTitle = computed<string>(() => {
  const raid = store.raids.find((candidate) => candidate.id === store.activeRaidId)
  return raid === undefined
    ? messages.value.drawer.titleFallback
    : messages.value.drawer.titleForRaid(raid.name)
})

function retryLoad(): void {
  void store.fetchRaidMapPlan()
}

function moveGroup(raidGroupId: string, xBasisPoints: number, yBasisPoints: number): void {
  if (!canEdit.value) return
  void store.setRaidMapGroupPosition(raidGroupId, xBasisPoints, yBasisPoints)
}

function moveTree(xBasisPoints: number, yBasisPoints: number): void {
  if (!canEdit.value) return
  void store.setRaidMapTreePosition(xBasisPoints, yBasisPoints)
}

function editTag(tagKey: string): void {
  if (!canEdit.value) return
  const tag = store.playerTags.find((candidate) => candidate.seedKey === tagKey)
  if (tag !== undefined) emit('edit-tag', tag.id)
}

function groupIdsForRoom(roomStableId: string): ReadonlySet<string> {
  const room = store.currentRooms.find((candidate) => candidate.id === roomStableId)
  return new Set(room?.groupIds ?? [])
}
</script>
